import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Mail, Send, Calendar, FileText, CheckCircle2, ArrowRight, Users } from "lucide-react";
import { toast } from "sonner";
import AppLinkBanner from "@/components/AppLinkBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const [newsletters, setNewsletters] = useState([]);
  const [img, setImg] = useState({});
  const [c, setC] = useState({});
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [marketingTeam, setMarketingTeam] = useState([]);

  useEffect(() => {
    axios.get(`${API}/newsletters`).then(r => setNewsletters(r.data)).catch(() => {});
    axios.get(`${API}/site-images`).then(r => setImg(r.data)).catch(() => {});
    axios.get(`${API}/site-content`).then(r => setC(r.data)).catch(() => {});
    axios.get(`${API}/team`).then(r => {
      const mkt = r.data.filter(m => m.category === "Marketing Chair");
      setMarketingTeam(mkt);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please fill in all fields."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, form);
      toast.success(res.data.message);
      setSubscribed(true);
      setForm({ name: "", email: "" });
    } catch { toast.error("Failed to subscribe."); }
    finally { setLoading(false); }
  };

  const featured = newsletters.find(n => n.category === 'current') || null;
  const archive = newsletters.filter(n => n.category !== 'current');
  const cleanLink = (link) => link ? link.replace(/^#+/, '') : '';

  return (
    <div>
      {/* Page Header */}
      <section className="page-header">
        <div className="container-main">
          <p className="text-slate-400 text-xs font-medium uppercase tracking-widest mb-3">Outreach & Marketing</p>
          <h1 data-testid="newsletter-page-title" className="text-4xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Marketing</h1>
          <p className="text-slate-300 mt-4 text-base max-w-2xl">{c.newsletterHeaderDesc || "Stay up-to-date with the latest from Texas BHA — events, projects, opportunities, and more."}</p>
        </div>
      </section>

      {/* Application Link */}
      <AppLinkBanner link={c.mediaAppLink} dueDate={c.mediaAppDue} testId="media-application" />

      {/* Featured / Latest Article */}
      {featured && (
        <section data-testid="featured-newsletter" className="py-16 lg:py-20 bg-white">
          <div className="container-main">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">Latest Issue</p>
            <Card className="overflow-hidden border border-slate-100 shadow-md">
              <div className="grid md:grid-cols-2">
                <div className="h-64 md:h-auto bg-slate-100 overflow-hidden">
                  <img
                    src={img.newsletterHero}
                    alt={featured.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-8 lg:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-xs text-slate-400">{featured.date && new Date(featured.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {featured.title}
                  </h2>
                  <p className="text-slate-500 text-[15px] mt-3 leading-relaxed">{featured.preview}</p>
                  {cleanLink(featured.link) ? (
                    <a href={cleanLink(featured.link)} target="_blank" rel="noopener noreferrer" data-testid="featured-newsletter-link">
                      <Button className="mt-6 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-full px-6 text-sm font-semibold w-fit">
                        Read Full Issue <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  ) : (
                    <Button className="mt-6 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-full px-6 text-sm font-semibold w-fit opacity-50 cursor-not-allowed">
                      Read Full Issue <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                </CardContent>
              </div>
            </Card>
          </div>
        </section>
      )}

      <Separator className="bg-slate-100" />

      {/* Archive — Bottom */}
      <section data-testid="newsletter-archive" className="py-16 lg:py-20 bg-white">
        <div className="container-main max-w-4xl">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="h-5 w-5 text-[#0F172A]" />
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Past Issues</p>
          </div>
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>Newsletter Archive</h2>
          <div className="space-y-3">
            {archive.map((nl, idx) => (
              <Card key={nl.id || idx} data-testid={`newsletter-archive-${idx}`} className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-[15px] text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>{nl.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="h-3 w-3 text-slate-400" />
                        <span className="text-xs text-slate-400">{nl.date && new Date(nl.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                      </div>
                      <p className="text-slate-500 text-sm mt-2">{nl.preview}</p>
                    </div>
                    {cleanLink(nl.link) ? (
                      <a href={cleanLink(nl.link)} target="_blank" rel="noopener noreferrer" data-testid={`newsletter-read-${idx}`}>
                        <Button variant="outline" size="sm" className="rounded-full text-xs shrink-0 border-slate-200">
                          Read <ArrowRight className="ml-1 h-3 w-3" />
                        </Button>
                      </a>
                    ) : (
                      <Button variant="outline" size="sm" className="rounded-full text-xs shrink-0 border-slate-200 opacity-50 cursor-not-allowed">
                        Read <ArrowRight className="ml-1 h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {archive.length === 0 && newsletters.length <= 1 && (
              <p className="text-slate-400 text-sm text-center py-8">No archived newsletters yet.</p>
            )}
          </div>
        </div>
      </section>

      {/* Marketing Team */}
      {marketingTeam.length > 0 && (
        <>
          <Separator className="bg-slate-100" />
          <section className="py-16 lg:py-20 bg-slate-50" data-testid="marketing-team-section">
            <div className="container-main">
              <div className="flex items-center gap-3 mb-2">
                <Users className="h-5 w-5 text-[#0F172A]" />
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Our Team</p>
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>Marketing Chairs</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
                {marketingTeam.map((member, i) => (
                  <div key={member.id || i} data-testid={`marketing-member-${i}`} className="text-center group">
                    <div className="w-28 h-28 mx-auto rounded-full overflow-hidden bg-slate-200 border-2 border-white shadow-md mb-3">
                      {member.photo_url ? (
                        <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {member.name?.charAt(0)}
                        </div>
                      )}
                    </div>
                    <h3 className="font-semibold text-sm text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>{member.name}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{member.role || "Marketing Chair"}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
